import React, { useState, useEffect, useRef } from "react";
import "./Header.css";
import logo from "../../assets/Okiri-logo-rmbg.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { resolveStoreBadges } from "../../utils/storeLinks";

const DOWNLOAD_LINKS = [
  {
    name: "Palm Store",
    href: null,
    note: "Palm Store",
  },
  {
    name: "Samsung Galaxy Store",
    href: "http://apps.samsung.com/appquery/appDetail.as?appId=com.okirimobile.app",
    note: "Official store listing",
  },
  {
    name: "App Store",
    href: "https://apps.apple.com/ng/app/okiri-mobile/id6760467395",
    note: "Download on the App Store",
  },
  {
    name: "APKPure (Direct APK)",
    href: "https://d.apkpure.com/b/APK/com.okirimobile.app?versionCode=2",
    note: "Direct download",
  },
];

// Navigation items definition — single source of truth
const NAV_ITEMS = [
  { label: "Rate",       id: "pricing"  },
  { label: "Features",   id: "features" },
  { label: "FAQs",       id: "faqs"     },
  { label: "Contact Us", id: "footer"   },
];

function Header() {
  const [isMenuOpen,     setIsMenuOpen]     = useState(false);
  const [isScrolled,     setIsScrolled]     = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const navRef        = useRef(null);

  const downloadLinks = resolveStoreBadges(DOWNLOAD_LINKS);
  const hamburgerRef  = useRef(null);
  const downloadRef   = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ── Scroll detection ───────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Close mobile menu on outside click ────────────────────
  useEffect(() => {
    if (!isMenuOpen) return;

    const onDown = (e) => {
      const inside =
        navRef.current?.contains(e.target) ||
        hamburgerRef.current?.contains(e.target) ||
        downloadRef.current?.contains(e.target);
      if (!inside) setIsMenuOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isMenuOpen]);

  // ── Lock body scroll when drawer open ─────────────────────
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  // ── Close download dropdown on outside click / ESC ────────
  useEffect(() => {
    if (!isDownloadOpen) return;

    const onDown = (e) => {
      if (!downloadRef.current?.contains(e.target)) setIsDownloadOpen(false);
    };
    const onKey  = (e) => { if (e.key === "Escape") setIsDownloadOpen(false); };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown",   onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown",   onKey);
    };
  }, [isDownloadOpen]);

  // ── Opening mobile menu closes download dropdown ──────────
  useEffect(() => {
    if (isMenuOpen) setIsDownloadOpen(false);
  }, [isMenuOpen]);

  // ── Helpers ───────────────────────────────────────────────
  const closeAll = () => {
    setIsMenuOpen(false);
    setIsDownloadOpen(false);
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleNavSection = (id) => (e) => {
    e.preventDefault();
    closeAll();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 60);
    } else {
      scrollToId(id);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    closeAll();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("top"), 30);
    } else {
      scrollToId("top");
    }
  };

  const handleDownloadPick = () => closeAll();

  return (
    <>
      {/* Dark overlay behind mobile drawer */}
      <div
        className={`header__overlay ${isMenuOpen ? "visible" : ""}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />

      <header
        className={`header ${isScrolled ? "header--scrolled" : ""} ${isMenuOpen ? "header--menu-open" : ""}`}
      >
        <div className="header__container">

          {/* Logo */}
          <a href="#top" className="header__logoBox" onClick={handleLogoClick}>
            <img
              src={logo}
              alt="Okiri"
              className="header__logoImg"
              draggable="false"
            />
          </a>

          {/* Nav drawer (desktop: inline, mobile: slide-in) */}
          <nav
            ref={navRef}
            className={`header__nav ${isMenuOpen ? "open" : ""}`}
            aria-label="Main navigation"
          >
            <div className="nav-links">
              {NAV_ITEMS.map(({ label, id }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={handleNavSection(id)}
                >
                  {label}
                </a>
              ))}

              <Link to="/enterprise" onClick={closeAll} className="nav-link">
                Enterprise
              </Link>

              {/* Downloads block — only visible inside mobile drawer */}
              <div className="mobile-downloads">
                <div className="mobile-downloads__title">Download App</div>
                <div className="mobile-downloads__list">
                  {downloadLinks.map((d) => (
                    <a
                      key={d.name}
                      className="mobile-downloads__item"
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleDownloadPick}
                    >
                      <span className="mobile-downloads__name">
                        {d.name}
                        {d.recommended ? (
                          <span style={{ display: "block", width: "fit-content", marginTop: 4, padding: "1px 6px", borderRadius: 999, fontSize: "0.7em", fontWeight: 700, background: "#7c5cff", color: "#fff", whiteSpace: "nowrap" }}>★ For your device</span>
                        ) : null}
                      </span>
                      {d.note && (
                        <span className="mobile-downloads__note">{d.note}</span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* Right actions */}
          <div className="header__actions">

            {/* Download dropdown */}
            <div className="download" ref={downloadRef}>
              <button
                className="download-button"
                onClick={() => setIsDownloadOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={isDownloadOpen ? "true" : "false"}
                type="button"
              >
                <span>Download</span>
                <span className={`download-caret ${isDownloadOpen ? "open" : ""}`}>
                  ▾
                </span>
              </button>

              {isDownloadOpen && (
                <div className="download-menu" role="menu">
                  {downloadLinks.map((d) => (
                    <a
                      key={d.name}
                      className="download-menu__item"
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      onClick={handleDownloadPick}
                    >
                      <div className="download-menu__name">
                        {d.name}
                        {d.recommended ? (
                          <span style={{ display: "block", width: "fit-content", marginTop: 4, padding: "1px 6px", borderRadius: 999, fontSize: "0.7em", fontWeight: 700, background: "#7c5cff", color: "#fff", whiteSpace: "nowrap" }}>★ For your device</span>
                        ) : null}
                      </div>
                      {d.note && (
                        <div className="download-menu__note">{d.note}</div>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              ref={hamburgerRef}
              className={`hamburger ${isMenuOpen ? "open" : ""}`}
              onClick={() => setIsMenuOpen((v) => !v)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen ? "true" : "false"}
              type="button"
            >
              <span />
              <span />
              <span />
            </button>

          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
